import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect } from 'react';

import { ENDPOINT } from '~/constants/endpoints';
import {
  PAGINATION_PAGE_SIZE_DEFAULT,
  type PaginationSortDirection,
} from '~/constants/pagination';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { toSnakeCase, withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import {
  createInfiniteQuery,
  type InfiniteQueryOptionsType,
} from '~/utils/createInfiniteQuery';

import { queryKeysVehicle } from './queryKeys';

export type VehiclesQueryParams = {
  limit: number;
  offset: number;
  searchString?: string;
  filterActive?: boolean;
  filterCompany?: string;
  filterNotParentOu?: UUID;
  filterParentOu?: UUID;
  orderBy:
    | 'id'
    | 'license_plate'
    | 'company_id'
    | 'company_name'
    | 'created_on'
    | 'modified_on';
  returnTotalCount: boolean;
  sort: PaginationSortDirection;
};

const defaultQueryParams: VehiclesQueryParams = {
  filterActive: undefined,
  filterCompany: undefined,
  filterNotParentOu: undefined,
  filterParentOu: undefined,
  limit: PAGINATION_PAGE_SIZE_DEFAULT,
  offset: 0,
  orderBy: 'license_plate',
  returnTotalCount: true,
  searchString: undefined,
  sort: 'ASC',
};

export type VehicleListItem = {
  companyId: UUID;
  companyName: string;
  createdOn: string;
  id: UUID;
  isActive: boolean;
  licensePlate: string;
  modifiedOn: string;
};

type VehicleResponse = {
  data: readonly VehicleListItem[];
  hasNextPage: boolean;
  paginatedCount: number;
  totalCount: number;
};

/**
 * Generates the query options for the vehicles query so they can be shared between the useQuery hook and the prefetching.
 */
export const getVehiclesQueryOptions = ({
  queryParams,
  options,
}: {
  queryParams: Partial<VehiclesQueryParams>;
  options?: Omit<UseQueryOptions<VehicleResponse>, 'queryKey' | 'queryFn'>;
}) => {
  const qp = {
    ...defaultQueryParams,
    ...queryParams,
  };

  qp.sort = qp.sort?.toUpperCase();

  return {
    queryFn: async () => fetchVehicles(qp),
    queryKey: queryKeysVehicle.getAll(qp),
    staleTime: ms('60s'), // prevent hitting the rate limit of 30 requests/s
    ...options,
  };
};

/**
 * Fetches all vehicles from the API.
 * @see https://app.dev.vestigas.com/redoc#tag/Vehicle/operation/get_vehicles_vehicle_get
 */
export const fetchVehicles = async (
  queryParams: Partial<VehiclesQueryParams>,
) => {
  const mergedParams = {
    ...defaultQueryParams,
    ...queryParams,
  };

  const qp = withoutObjectKeysWhereValueIs(
    {
      ...mergedParams,
      orderBy: mergedParams.orderBy
        ? toSnakeCase(mergedParams.orderBy)
        : undefined,
      sort: mergedParams.sort ? mergedParams.sort.toUpperCase() : undefined,
    },
    [undefined],
  );

  const response = await vestigasApi
    .get(ENDPOINT.VEHICLE.GET_ALL(), {
      searchParams: qp,
    })
    .json<VehicleResponse>();

  return (
    response ?? {
      data: [],
      hasNextPage: false,
      paginatedCount: 0,
      totalCount: 0,
    }
  );
};

/**
 * React Query based custom hook for getting the data for all vehicles with given query parameters.
 * Handles pagination and prefetches the next page of vehicles for better performance.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Vehicle/operation/get_vehicles_vehicle_get
 */
export const useQueryVehicles = (
  queryParams: Partial<VehiclesQueryParams>,
  options?: Omit<UseQueryOptions<VehicleResponse>, 'queryKey' | 'queryFn'>,
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (queryParams?.searchString?.length) {
      // Only prefetch if the user is not searching, to prevent firing lots of requests for costly queries.
      return;
    }

    queryClient.prefetchQuery(
      getVehiclesQueryOptions({
        options,
        queryParams: {
          ...queryParams,
          offset:
            (queryParams.offset ?? 0) +
            (queryParams.limit ?? defaultQueryParams.limit),
        },
      }),
    );
  }, [queryParams, options, queryClient]);

  return useQuery({
    ...getVehiclesQueryOptions({ options, queryParams }),
    placeholderData: keepPreviousData,
  });
};

/**
 * React Query based custom hook for getting the data for all vehicles with infinite scrolling.
 * Uses useInfiniteQuery to handle loading more data as the user scrolls.
 *
 * @param {Object} queryParams - The query parameters for fetching vehicles.
 * @param {Object} options - Additional options for the useInfiniteQuery hook.
 * @returns {UseInfiniteQueryResult} The result of the useInfiniteQuery hook.
 */
export const useQueryVehiclesInfinite = (
  queryParams: Partial<VehiclesQueryParams>,
  options?: InfiniteQueryOptionsType<VehicleResponse, Error>,
) => {
  const qp = {
    ...defaultQueryParams,
    ...queryParams,
  };

  const infiniteQueryOptions = {
    queryFn: async ({ pageParam: pageParameter = 0 }) =>
      fetchVehicles({
        ...qp,
        offset: pageParameter,
      }),
    queryKey: queryKeysVehicle.getAllInfinite(queryParams),
    ...options,
  };

  return createInfiniteQuery<VehicleResponse>(qp, infiniteQueryOptions);
};
